import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaShieldAlt, FaCalendarAlt, FaPiggyBank, FaChartLine } from 'react-icons/fa';

const RetirementPlanningPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Retirement Planning' }
  ];

  const retirementServices = [
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Retirement Security",
      description: "Build a secure financial foundation for your retirement years with comprehensive planning strategies."
    },
    {
      icon: <FaCalendarAlt className="w-8 h-8 text-secondary" />,
      title: "Timeline Planning",
      description: "Create a detailed timeline that aligns your retirement goals with your current financial situation."
    },
    {
      icon: <FaPiggyBank className="w-8 h-8 text-secondary" />,
      title: "Income Generation",
      description: "Develop strategies to generate sufficient income throughout your retirement years."
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Investment Optimization",
      description: "Optimize your investment portfolio to balance growth potential with risk management."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Retirement Planning" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/portrait-successful-young-businesswoman-with-colleagues.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Secure Your Retirement Future
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Whatever your financial needs are, at <span className="font-semibold">Segway Capital</span> we help you figure out the right type of 
                investments to help generate enough income and plan your finances efficiently for a comfortable 
                retirement. Our comprehensive approach ensures you're prepared for every stage of your retirement journey.
              </p>
              <p className="text-lg text-gray-700">
                We understand that retirement planning is not just about accumulating wealth—it's about creating 
                a sustainable income stream that will support your desired lifestyle throughout your golden years.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/workmates-with-statistic-documents.jpg" 
                alt="Retirement Planning Analysis" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {retirementServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
              >
                <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-custom mb-16"
          >
            <h3 className="text-3xl font-heading font-bold text-primary mb-8 text-center">
              Retirement Planning Essentials
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Early Career (20s-30s)</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Start saving early to maximize compound growth
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Take advantage of employer-sponsored retirement plans
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Focus on aggressive growth investments
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Mid-Career (40s-50s)</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Increase savings rate and catch-up contributions
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Diversify investment portfolio
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Plan for healthcare and long-term care costs
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Pre-Retirement (60s)</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Shift to more conservative investments
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Create withdrawal strategy
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Optimize Social Security timing
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-primary">Retirement (70+)</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Implement income distribution strategy
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Manage tax-efficient withdrawals
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2">•</span>
                    Plan for estate and legacy considerations
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Plan Your Retirement Today</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Don't wait to start planning for your retirement. Our retirement planning experts are here to 
                help you create a comprehensive strategy that ensures a comfortable and secure future.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Start Planning Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default RetirementPlanningPage; 